import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { Grid } from '@mui/material';
import classnames from 'classnames';
import each from 'lodash/each';
import find from 'lodash/find';

import { useSelector } from 'store';
import { useAppNavigate } from 'hooks';
import { Container, Breadcrumbs } from 'components/controls';
import WiseDashboardMenu from 'components/wise/WiseDashboardMenu';
import {
  userRolesAccessRoutes,
  filterAccessRoutesByPermissions
} from 'components/layouts/DashboardSubLayout/SubLayoutDashboard';

import styles from 'theme/components/layouts/WiseDashboardSubLayout/WiseDashboardSubLayout.module.scss';

interface IProps {
  urlPrefix: string;
}

interface IRoute {
  label: string;
  path: string;
  key: string | null;
}

const WiseDashboardSubLayout: FC<IProps> = ({ urlPrefix }) => {
  const { profile } = useSelector((state) => state.auth);
  const navigate = useAppNavigate();
  const { pathname } = useLocation();

  const [isCondensed, setIsCondensed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const companyUserSettings = profile?.additional_information?.company_user_settings;

  const filteredAccessRoutes = profile?.role
    ? filterAccessRoutesByPermissions(
        userRolesAccessRoutes()[`${profile.role}`],
        profile.role,
        companyUserSettings
      )
    : null;

  const allowRoute = useCallback(() => {
    if (!filteredAccessRoutes) {
      return true;
    }
    let arr: IRoute[] = [{ label: 'Dashboard', path: '/dashboard', key: null }];

    each(filteredAccessRoutes, (route) => (arr = [...arr, ...route]));

    return !!find(arr, (route) => route.key && pathname.includes(route.key));
  }, [pathname, profile?.role, filteredAccessRoutes]);

  useEffect(() => {
    if (pathname === `${urlPrefix}/dashboard`) {
      return;
    }
    if (!profile?.role) {
      return;
    }
    if (!allowRoute()) {
      navigate('/dashboard');
    }
  }, [pathname, profile?.role]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Layouts-WiseDashboardSubLayout'
      )}>
      <Container>
        <Breadcrumbs />
        <Grid container columnSpacing="24px">
          <Grid
            item
            md={isCondensed ? 1 : 2.5}
            xs={12}
            className={classnames(styles.menuWrapper, {
              [styles.menuWrapperCondensed]: isCondensed
            })}>
            {profile?.role && filteredAccessRoutes ? (
              <WiseDashboardMenu
                accessRoutes={filteredAccessRoutes}
                isCondensed={isCondensed}
                onToggleCondensed={() => setIsCondensed((v) => !v)}
              />
            ) : (
              <div className={styles.menuSkeleton}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={styles.menuSkeletonItem}>
                    <div className={styles.menuSkeletonLine} style={{ width: `${60 + (i % 3) * 15}%` }} />
                  </div>
                ))}
              </div>
            )}
          </Grid>
          <Grid item md={isCondensed ? 11 : 9.5} xs={12}>
            <div ref={contentRef} className={styles.content}>
              <Outlet />
            </div>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default WiseDashboardSubLayout;
